import { State, process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { Field, Form, FormElement, FormRenderProps } from "@progress/kendo-react-form";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
} from "@progress/kendo-react-grid";
import { Error } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BetaValuesEditor } from ".";
import marginsApi from "../../../api/margins";
import { useApi } from "../../../hooks/useApi";
import { useUserContext } from "../../../hooks/useUserContext";
import { requiredValidator } from "../../../validators";
import { FormDropDownList, FormInput } from "../../form";
import DeleteDataItemDialog from "../../grid/DeleteDataItemDialog";
import { LoadingIndicator } from "../../LoadingIndicator";
import { ICommodity } from "../../quotes";
import { IProductFilters } from "../configurations";
import { BetaValue } from "./BetaValue";

const COPY_MODE = "copy";
const NEW_MODE = "new";

interface IMarginsGridProps {
  businessTypesList: any[];
  commodities: ICommodity[];
  filter: IProductFilters | null;
  onEdit: (editing: boolean) => void;
}

export const MarginsGrid = ({
  businessTypesList,
  commodities,
  filter,
  onEdit,
}: IMarginsGridProps) => {
  const { context: user } = useUserContext();

  const getMarginsApi = useApi(marginsApi.getMargins);
  const deleteMarginsApi = useApi(marginsApi.deleteMargin);
  const saveBetasApi = useApi(marginsApi.updateBetas);
  const saveMarginApi = useApi(marginsApi.createMargin);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [gridData, setGridData] = useState<any[]>([]);
  const [result, setResult] = useState<any>([]);
  const [dataState, setDataState] = useState<State>({
    skip: 0,
    take: 10,
  });

  const [selectedId, setSelectedId] = useState(0);
  const [deletedId, setDeletedId] = useState(0);
  const [mode, setMode] = useState("");
  const [betasData, setBetasData] = useState<BetaValue[]>([]);
  const [commoditiesList, setCommoditiesList] = useState<any[]>([]);
  const [commodityUnit, setCommodityUnit] = useState("");
  const [initialValues, setInitialValues] = useState<any | null>(null);

  const { itemId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getMargins();
  }, []);

  const getMargins = async () => {
    await getMarginsApi.request();
  };

  useEffect(() => {
    setLoading(getMarginsApi.loading);
  }, [getMarginsApi.loading]);

  useEffect(() => {
    if (itemId && getMarginsApi.data) {
      const margin = getMarginsApi.data.margins.find((m: any) => m.marginId === parseInt(itemId));
      if (margin) setSelectedId(margin.marginId);
    }
  }, [itemId, getMarginsApi.data]);

  useEffect(() => {
    if (commodities) {
      setCommoditiesList(
        [{ id: null, name: " " }]
          .concat(commodities)
          .map((c: any) => ({ value: c.id, label: c.name }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
      );
    }
  }, [commodities]);

  useEffect(() => {
    const margin = gridData.find((m: any) => m.marginId === selectedId);

    if (!margin) {
      setInitialValues(
        businessTypesList.length === 1 ? { businessType: businessTypesList[0] } : null,
      );
    } else {
      handleChangeCommodity(margin.commodityId);
      setInitialValues({
        name: mode === COPY_MODE ? "" : margin.longName,
        commodity: commoditiesList.find((c) => c.value === margin.commodityId),
        businessType: businessTypesList.find((b) => b.value === margin.businessTypeId),
      });
    }
  }, [selectedId, commoditiesList, businessTypesList]);

  useEffect(() => {
    const searchText = filter?.text.toLowerCase() ?? "";
    const businessTypeFilter = filter?.businessType ?? 0;
    const commoditiesFilter = filter?.commodities ?? [];
    const marginsData = getMarginsApi.data?.margins;

    if (marginsData && commodities) {
      setGridData(
        marginsData
          .filter((m: any) => selectedId === 0 || m.marginId === selectedId)
          .map((margin: any) => {
            const selected = margin.marginId === selectedId;
            const commodity = margin.commodityId
              ? commodities.find((c: ICommodity) => c.id === margin.commodityId)
              : null;
            return { selected, commodity, ...margin };
          })
          .filter((m: any) => businessTypeFilter === 0 || m.businessTypeId === businessTypeFilter)
          .filter(
            (m: any) =>
              commoditiesFilter.length === 0 || commoditiesFilter.includes(m.commodity?.type),
          )
          .filter(
            (m: any) =>
              searchText.length === 0 ||
              m.longName.toLowerCase().includes(searchText) ||
              m.businessType.toLowerCase().includes(searchText) ||
              m.commodity?.name.toLowerCase().includes(searchText),
          ),
      );
    }
  }, [getMarginsApi.data, commodities, selectedId, filter]);

  useEffect(() => {
    applyDataStateChange(dataState);
  }, [gridData]);

  useEffect(() => {
    setLoading(saveBetasApi.loading);
    setError(saveBetasApi.error);

    //TODO: Figure out why data is coming back as undefined here
    // if (!saveBetasApi.loading && (saveBetasApi.data || saveBetasApi.error))
    //   if (!saveBetasApi.error)

    if (!saveBetasApi.loading && !saveBetasApi.error) handleCancel();
  }, [saveBetasApi.loading, saveBetasApi.data, saveBetasApi.error]);

  useEffect(() => {
    setLoading(saveMarginApi.loading);
    setError(saveMarginApi.error);

    if (!saveMarginApi.loading && (saveMarginApi.data || saveMarginApi.error))
      if (!saveMarginApi.error) handleCancel();
  }, [saveMarginApi.loading, saveMarginApi.data, saveMarginApi.error]);

  useEffect(() => {
    setLoading(deleteMarginsApi.loading);
    setError(deleteMarginsApi.error);

    if (!deleteMarginsApi.loading && (deleteMarginsApi.data || deleteMarginsApi.error))
      if (!deleteMarginsApi.error) getMargins();
  }, [deleteMarginsApi.loading, deleteMarginsApi.data, deleteMarginsApi.error]);

  useEffect(() => {
    if (loading && error) setError("");
  }, [loading]);

  useEffect(() => {
    const isEditing = !(selectedId === 0 && mode !== NEW_MODE);
    onEdit && onEdit(isEditing);
  }, [selectedId, mode]);

  const dataStateChange = (event: GridDataStateChangeEvent) => {
    applyDataStateChange(event.dataState);
  };

  const applyDataStateChange = (newDataState: State) => {
    setResult(process(gridData.slice(0), newDataState));
    setDataState(newDataState);
  };

  const handleEditMargin = (dataItem: any) => setSelectedId(dataItem?.marginId);

  const handleNewMargin = () => {
    setSelectedId(0);
    setMode(NEW_MODE);
  };

  const handleCopyMargin = (dataItem: any) => {
    setSelectedId(dataItem?.marginId);
    setMode(COPY_MODE);
  };

  const handleDeleteMargin = (dataItem: any) => {
    setDeletedId(dataItem?.marginId);
    deleteMarginsApi.request(dataItem?.marginId);
  };

  const handleSaveMargin = async (event: any) => {
    const formData = { ...event };

    if (mode === NEW_MODE || mode === COPY_MODE) {
      await saveMarginApi.request(
        formData.name,
        betasData.filter((d: any) => d.isActive),
        formData.commodity?.value,
        formData.businessType.value,
      );
    } else {
      const data = betasData
        .filter((d: any) => d.isModified)
        .map((d: any) => (d.contractMonth?.length > 0 ? d : { ...d, contractMonth: null }));

      await saveBetasApi.request(data, formData.name, selectedId);
    }
  };

  const handleCancel = () => {
    setSelectedId(0);
    setBetasData([]);
    setMode("");
    setError("");
    getMargins();
  };

  const handleCancelEdit = () => {
    handleCancel();
    navigate("/admin/margins");
  };

  const handleChangeCommodity = (commodityId: number) => {
    const commodity = (commodities ?? []).find((c: ICommodity) => c.id === commodityId);
    setCommodityUnit((commodity?.units ?? "s").slice(0, -1));
  };

  const handleEdits = (formRenderProps: FormRenderProps, data: BetaValue[]) => {
    const edits = data.filter((d) => d.isModified).map((d) => d.betaId);
    if (edits.length > 0) formRenderProps.onChange("edits", { value: edits.join(",") });
    setBetasData(data);
  };

  const ActionsCell = (props: GridCellProps) => {
    const { dataItem } = props;
    const field = props.field || "";
    const value = dataItem[field];

    return (
      <td className="k-table-td">
        {selectedId === 0 && (
          <div style={{ display: "flex" }}>
            <Button
              icon="pencil"
              themeColor="primary"
              fillMode="outline"
              title="Edit"
              size="medium"
              onClick={() => handleEditMargin(dataItem)}
            />

            <Button
              icon="copy"
              themeColor="primary"
              fillMode="outline"
              title="Copy"
              size="small"
              onClick={() => handleCopyMargin(dataItem)}
            />

            {!value && (
              <DeleteDataItemDialog
                description={dataItem.longName}
                disabled={loading}
                onDelete={() => handleDeleteMargin(dataItem)}
              />
            )}
          </div>
        )}
      </td>
    );
  };

  return (
    <>
      <LoadingIndicator loading={loading} />
      {getMarginsApi.error && <Error>{getMarginsApi.error}</Error>}
      {error && <Error>{error}</Error>}

      {!loading && (
        <>
          {selectedId === 0 && mode !== NEW_MODE && (
            <div
              style={{
                display: "flex",
                alignItems: "end",
                justifyContent: "space-between",
                marginBottom: "1rem",
              }}
            >
              <Button
                themeColor="primary"
                size="small"
                onClick={handleNewMargin}
                style={{ marginBottom: 10 }}
              >
                Add Margin
              </Button>
            </div>
          )}

          {mode !== NEW_MODE && (
            <Tooltip anchorElement="target" parentTitle={true}>
              <Grid
                data={result}
                {...dataState}
                pageable={selectedId === 0}
                resizable={true}
                sortable={true}
                onDataStateChange={dataStateChange}
                selectedField="selected"
              >
                <GridColumn field="longName" title="Margin Name" />
                <GridColumn field="businessType" title="Business Type" />
                <GridColumn field="commodity.name" title="Commodity" />
                <GridColumn field="isInUse" title=" " cells={{ data: ActionsCell }} width={100} />
              </Grid>
            </Tooltip>
          )}
        </>
      )}

      {(selectedId > 0 || mode === NEW_MODE) && (
        <>
          {(initialValues !== null || mode === NEW_MODE) && (
            <Form
              initialValues={initialValues}
              onSubmit={handleSaveMargin}
              render={(formRenderProps) => (
                <FormElement>
                  <div
                    style={{ display: "flex", gap: 10, marginTop: "1rem", alignItems: "flex-end" }}
                  >
                    <Field
                      label="Margin Name"
                      name="name"
                      component={FormInput}
                      validator={requiredValidator}
                      disabled={loading}
                      maxLength={255}
                      style={{ minWidth: 300 }}
                    />

                    {(mode === NEW_MODE || mode === COPY_MODE) && (
                      <>
                        <Field
                          label="Business Type"
                          name="businessType"
                          component={FormDropDownList}
                          validator={requiredValidator}
                          data={businessTypesList}
                          disabled={loading}
                          textField="label"
                          dataItemKey="value"
                          style={{ minWidth: 200 }}
                        />

                        <Field
                          label="Commodity"
                          name="commodity"
                          component={FormDropDownList}
                          data={commoditiesList}
                          disabled={loading}
                          textField="label"
                          dataItemKey="value"
                          onChange={(e) => handleChangeCommodity(e.target.value.value)}
                          style={{ minWidth: 300 }}
                        />
                      </>
                    )}
                  </div>

                  <div style={{ margin: "1rem 0" }}>
                    <BetaValuesEditor
                      marginId={selectedId}
                      disabled={loading}
                      userRoles={user.roles}
                      unit={commodityUnit}
                      onDataUpdate={(data) => handleEdits(formRenderProps, data)}
                    />
                  </div>

                  <Field
                    name="edits"
                    component={FormInput}
                    style={{ visibility: "hidden", display: "none" }}
                  />

                  <div>
                    <Button
                      themeColor="secondary"
                      fillMode="solid"
                      onClick={handleCancelEdit}
                      disabled={loading}
                    >
                      Cancel
                    </Button>

                    <Button
                      themeColor="primary"
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                    >
                      Save
                    </Button>
                  </div>
                </FormElement>
              )}
            ></Form>
          )}
        </>
      )}
    </>
  );
};
